import { hexA, type Item } from '@scribbleplot/shared';
import type { BoardSummary } from '@scribbleplot/shared';
import { color, font, TYPE_GLYPH } from '../tokens';

/**
 * Left rail: the user's other boards, and a layer list for this one.
 *
 * Layers are ordered front-to-back by z, matching what you see on the canvas,
 * and clicking one centres the camera on it — which is how you find something
 * you have zoomed away from.
 */

const labelFor = (item: Item): string => {
  switch (item.type) {
    case 'sticky':
      return item.text || 'Note';
    case 'text':
      return item.text || 'Text';
    case 'image':
      return item.name || 'Image';
    case 'frame':
      return item.label || 'Area';
    case 'group':
      return `${item.name} (${item.children.length})`;
    case 'link':
      return item.title || 'Link';
  }
};

export function Sidebar({
  boards,
  currentBoardId,
  items,
  sel,
  accent,
  onOpenBoard,
  onFocusItem,
}: {
  boards: BoardSummary[];
  currentBoardId: string | null;
  items: Item[];
  sel: readonly string[];
  accent: string;
  onOpenBoard: (id: string) => void;
  onFocusItem: (id: string) => void;
}) {
  // Front-most first, so the list reads the way the canvas stacks.
  const layers = [...items].sort((a, b) => b.z - a.z);

  return (
    <div
      style={{
        width: 230,
        flex: 'none',
        borderRight: `1px solid ${color.line}`,
        background: color.surfaceAlt,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      {/* Signed-out visitors on the demo have no board list; showing an empty
          heading would just be furniture. */}
      {boards.length > 0 && (
        <div
          style={{
            padding: '14px 14px 4px',
            fontSize: 10,
            fontFamily: font.mono,
            letterSpacing: '.12em',
            color: color.textFaint,
          }}
        >
          BOARDS
        </div>
      )}
      <div
        style={{
          padding: '4px 8px 0',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          maxHeight: 180,
          overflowY: 'auto',
        }}
      >
        {boards.map((board) => {
          const current = board.id === currentBoardId;
          return (
            <button
              key={board.id}
              type="button"
              onClick={() => !current && onOpenBoard(board.id)}
              aria-current={current ? 'true' : undefined}
              style={{
                padding: '6px 8px',
                borderRadius: 8,
                border: 'none',
                fontSize: 12.5,
                fontFamily: 'inherit',
                fontWeight: current ? 600 : 400,
                color: current ? color.text : color.textMuted,
                background: current ? color.hover : 'transparent',
                cursor: current ? 'default' : 'pointer',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textAlign: 'left',
              }}
            >
              {board.name}
            </button>
          );
        })}
      </div>

      <div
        style={{
          padding: '16px 14px 4px',
          fontSize: 10,
          fontFamily: font.mono,
          letterSpacing: '.12em',
          color: color.textFaint,
        }}
      >
        LAYERS
      </div>
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '4px 8px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {layers.map((item) => {
          const active = sel.includes(item.id);
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onFocusItem(item.id)}
              title={labelFor(item)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '5px 7px',
                borderRadius: 8,
                border: 'none',
                fontSize: 12.5,
                fontFamily: 'inherit',
                color: active ? color.text : color.textMuted,
                background: active ? hexA(accent, 0.12) : 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
                width: '100%',
              }}
            >
              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 5,
                  background: item.type === 'sticky' ? item.color : '#2A2C33',
                  color: item.type === 'sticky' ? color.ink : color.textDim,
                  fontSize: 9.5,
                  fontFamily: font.mono,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 'none',
                }}
              >
                {TYPE_GLYPH[item.type]}
              </span>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {labelFor(item)}
              </span>
            </button>
          );
        })}
        {!layers.length && (
          <div style={{ padding: 8, fontSize: 12, color: color.textGhost }}>Nothing on this board yet.</div>
        )}
      </div>
    </div>
  );
}
